import { useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Building2,
  Hotel,
  Home as HomeIcon,
  Phone,
  ArrowRight,
  Check,
  Send,
  AlertCircle,
  ShieldCheck,
  FileText,
  Clock,
  Users,
  Wrench,
  KeyRound,
  ClipboardList,
  Headphones,
  Download,
  Star,
  Quote,
  BadgeCheck,
} from "lucide-react";
import { COMPANY, PRO_PARTNERS, PRO_AUDIENCES, CITIES } from "../lib/constants";
import useSeo, { breadcrumbJsonLd, SITE } from "../lib/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRO_FAQ = [
  {
    q: "Proposez-vous un contrat annuel ou un devis cadre ?",
    a: "Oui — pour les syndics, agences et hôteliers, nous établissons un devis cadre avec grille tarifaire négociée, conditions de facturation pro (30 jours), et astreinte téléphonique dédiée. Audit gratuit sur 1 ou 2 sites pilotes avant signature.",
  },
  {
    q: "Quel délai d'intervention pour un parc immobilier ?",
    a: "30 minutes en moyenne sur le secteur Mont-Dol / Côte d'Émeraude / Côtes-d'Armor (22) et Ille-et-Vilaine (35) — y compris en astreinte de nuit, week-ends et jours fériés. Priorisation automatique des urgences locataires / résidents.",
  },
  {
    q: "Acceptez-vous les paiements à 30 jours ?",
    a: "Oui — facturation pro avec TVA, BAT, multi-sites, paiement à 30 jours fin de mois sur acceptation de dossier. Attestations sur demande pour vos comptables.",
  },
  {
    q: "Pouvez-vous gérer un organigramme de clés ?",
    a: "Oui — conception, fabrication et maintenance d'organigrammes pour parcs résidentiels et hôteliers, y compris remplacements partiels, ajout de niveaux d'accès et gestion des clés perdues.",
  },
  {
    q: "Sur quelles zones intervenez-vous ?",
    a: "Bretagne — toute la côte d'Émeraude, la baie du Mont-Saint-Michel et l'arrière-pays : Dinard, Saint-Malo, Dinan, Cancale, Dol-de-Bretagne, Pleurtuit, Saint-Briac, Saint-Cast-le-Guildo, Plancoët, Matignon, et l'ensemble du secteur 22 / 35 dans un rayon de 30 km autour de Mont-Dol.",
  },
];

const STRUCTURES = [
  {
    icon: Building2,
    title: "Syndics & copropriétés",
    desc: "Interventions sur parties communes, ouvertures, sécurisation, audits annuels et gestion des urgences résidents.",
  },
  {
    icon: HomeIcon,
    title: "Agences immobilières",
    desc: "Remise en état entre locataires, ouvertures pour états des lieux, changement de cylindres, mise en sécurité après dégradation.",
  },
  {
    icon: Hotel,
    title: "Hôtellerie & locations courte durée",
    desc: "Boîtes à clés, serrures à code, dépannage 24h/24 pour clients bloqués, maintenance préventive saisonnière.",
  },
];

const VALUE_PROPS = [
  {
    icon: Users,
    t: "Référent unique",
    d: "Un seul interlocuteur pour tout votre parc — réponse sous 1h en jours ouvrés.",
  },
  {
    icon: FileText,
    t: "Devis sous 24h",
    d: "Estimations chiffrées rapides, formats compatibles avec vos outils de gestion.",
  },
  {
    icon: Clock,
    t: "Astreinte 24/7",
    d: "Une ligne dédiée pour les urgences résidents / clients, week-ends et jours fériés inclus.",
  },
  {
    icon: ShieldCheck,
    t: "Facturation pro",
    d: "TVA, BAT, multi-sites, paiement à 30 jours possible, attestations sur demande.",
  },
];

const PRO_SERVICES = [
  {
    icon: KeyRound,
    t: "Ouvertures & rotations de cylindres",
    d: "Changements lors de chaque rotation locative, gestion d'organigrammes, clés bloquées sur demande.",
  },
  {
    icon: ShieldCheck,
    t: "Sécurisation logements vides",
    d: "Mise en sécurité de logements en travaux ou en vacance locative, protection anti-squat.",
  },
  {
    icon: Wrench,
    t: "Maintenance préventive",
    d: "Visite annuelle de votre parc, contrôle des fermetures, lubrification, prévention des pannes coûteuses.",
  },
  {
    icon: ClipboardList,
    t: "Audits sécurité & rapports",
    d: "Diagnostic complet par bâtiment, recommandations chiffrées, livrable PDF pour conseil syndical.",
  },
  {
    icon: Headphones,
    t: "Gestion d'urgence parc",
    d: "Astreinte téléphonique avec priorisation, intervention sous 30 min sur le secteur 22 / 35.",
  },
  {
    icon: FileText,
    t: "Tarifs négociés & conventionnés",
    d: "Grilles tarifaires dégressives, contrats annuels, conditions spéciales pour gestionnaires multi-sites.",
  },
];

const STEPS = [
  { t: "Prise de contact", d: "Vous nous décrivez votre activité, votre parc et vos besoins récurrents." },
  { t: "Audit gratuit", d: "Visite sur 1 ou 2 sites pilotes pour évaluer l'état des fermetures et les enjeux." },
  { t: "Proposition sur-mesure", d: "Devis cadre, grille tarifaire, modalités d'astreinte et de facturation." },
  { t: "Mise en service", d: "Référent dédié, ligne directe, suivi mensuel et reporting d'interventions." },
];

const STRUCTURE_OPTIONS = [
  "Syndic / copropriété",
  "Agence immobilière",
  "Bailleur / foncière",
  "Hôtel / résidence",
  "Location courte durée",
  "Commerce / bureau",
  "Autre",
];

const EMPTY = {
  nom: "",
  entreprise: "",
  fonction: "",
  telephone: "",
  email: "",
  type_structure: "",
  nb_lots: "",
  besoin: "",
};

export default function Professionnels() {
  useSeo({
    title:
      "Serrurier pour professionnels — Syndics, agences immobilières, hôtels | Mon Serrurier Breton",
    description:
      "Service serrurerie B2B dédié aux gestionnaires de copropriété, agences immobilières et hôtellerie en Bretagne (22 & 35) : référent unique, astreinte 24/7, facturation pro à 30 jours, contrats cadres et tarifs négociés. Devis sous 24h.",
    path: "/professionnels",
    jsonLd: [
      breadcrumbJsonLd([
        { name: "Accueil", path: "/" },
        { name: "Professionnels", path: "/professionnels" },
      ]),
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "@id": `${SITE.url}/professionnels#service`,
        name: "Serrurerie pour professionnels — syndics, agences, hôtellerie",
        serviceType: "Locksmith services for property managers",
        provider: {
          "@type": "Locksmith",
          "@id": `${SITE.url}/#business`,
          name: SITE.name,
          telephone: "+33780939300",
          email: "contact@monserrurierbreton.fr",
          url: SITE.url,
          areaServed: [
            { "@type": "AdministrativeArea", name: "Côtes-d'Armor (22)" },
            { "@type": "AdministrativeArea", name: "Ille-et-Vilaine (35)" },
          ],
        },
        areaServed: [
          { "@type": "AdministrativeArea", name: "Côtes-d'Armor (22)" },
          { "@type": "AdministrativeArea", name: "Ille-et-Vilaine (35)" },
          { "@type": "City", name: "Dinard" },
          { "@type": "City", name: "Saint-Malo" },
          { "@type": "City", name: "Dinan" },
          { "@type": "City", name: "Mont-Dol" },
          { "@type": "City", name: "Ploubalay" },
        ],
        audience: [
          { "@type": "BusinessAudience", audienceType: "Syndic de copropriété" },
          { "@type": "BusinessAudience", audienceType: "Agence immobilière" },
          { "@type": "BusinessAudience", audienceType: "Bailleur / foncière" },
          { "@type": "BusinessAudience", audienceType: "Hôtellerie / résidence de tourisme" },
        ],
        offers: {
          "@type": "Offer",
          priceCurrency: "EUR",
          priceSpecification: {
            "@type": "PriceSpecification",
            description: "Tarifs négociés sur contrat cadre, devis sous 24h",
          },
          availability: "https://schema.org/InStock",
        },
        hasOfferCatalog: {
          "@type": "OfferCatalog",
          name: "Services serrurerie B2B",
          itemListElement: [
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Ouvertures & rotations de cylindres locatifs" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Sécurisation logements vides ou en travaux" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Maintenance préventive annuelle de parc" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Audits sécurité & rapports PDF" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Astreinte 24/7 dédiée aux gestionnaires" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Organigrammes de clés multi-niveaux" } },
          ],
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: PRO_FAQ.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  });

  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [errors, setErrors] = useState({});

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.nom || form.nom.trim().length < 2) e.nom = "Nom requis";
    if (!form.entreprise || form.entreprise.trim().length < 2)
      e.entreprise = "Entreprise requise";
    if (!form.telephone || form.telephone.trim().length < 6)
      e.telephone = "Téléphone requis";
    if (!form.email || !/^\S+@\S+\.\S+$/.test(form.email))
      e.email = "Email pro requis";
    if (!form.type_structure) e.type_structure = "Type requis";
    if (!form.besoin || form.besoin.trim().length < 2)
      e.besoin = "Décrivez votre besoin";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Merci de compléter les champs obligatoires.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.fonction) delete payload.fonction;
      if (!payload.nb_lots) delete payload.nb_lots;
      await axios.post(`${API}/pro`, payload);
      toast.success("Demande envoyée — nous vous recontactons sous 24h ouvrées.");
      setSent(true);
      setForm(EMPTY);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Erreur à l'envoi. Merci d'appeler directement.";
      toast.error(typeof msg === "string" ? msg : "Erreur d'envoi");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = (k) =>
    `w-full rounded-xl border ${
      errors[k] ? "border-red-400" : "border-slate-300"
    } bg-white px-4 py-3 text-slate-900 outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100 transition`;

  return (
    <div data-testid="page-professionnels">
      <Toaster position="top-right" richColors />

      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-900 text-white">
        <div className="absolute inset-0 opacity-[0.07] [background-image:radial-gradient(#5eead4_1px,transparent_1px)] [background-size:18px_18px]" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-16 md:pt-24 pb-16 md:pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-300">
                <span className="h-px w-8 bg-teal-300" /> Espace professionnels
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[0.95]">
                Une serrurerie
                <br />
                <span className="text-teal-300">pour les pros</span>.
              </h1>
              <p className="text-slate-300 text-lg max-w-2xl leading-relaxed">
                Syndics, agences immobilières, hôteliers, gestionnaires de
                résidences : déléguez toute la serrurerie de votre parc à un
                artisan local, joignable 7j/7, avec un{" "}
                <strong className="text-white">référent dédié</strong>, une{" "}
                <strong className="text-white">facturation pro</strong> et des{" "}
                <strong className="text-white">tarifs conventionnés</strong>.
              </p>
              <div className="flex flex-wrap gap-3 pt-2">
                <a
                  data-testid="pro-hero-call"
                  href={`tel:${COMPANY.phoneDigits}`}
                  className="inline-flex items-center gap-3 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full px-7 py-4 shadow-2xl shadow-orange-600/30 transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  {COMPANY.phone}
                </a>
                <a
                  data-testid="pro-hero-form"
                  href="#pro-form"
                  className="inline-flex items-center gap-2 bg-white text-slate-900 font-bold rounded-full px-6 py-4 hover:bg-teal-50 transition-colors"
                >
                  Demander un devis cadre <ArrowRight className="w-4 h-4" />
                </a>
                <a
                  data-testid="pro-hero-brochure"
                  href={`${API}/pro/brochure.pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 border-2 border-slate-700 text-slate-200 hover:border-teal-300 hover:text-teal-300 font-bold rounded-full px-6 py-4 transition-colors"
                >
                  <Download className="w-4 h-4" /> Plaquette PDF
                </a>
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-2 pt-4 text-sm text-slate-300 border-t border-slate-800">
                <span className="flex items-center gap-2 pt-3">
                  <Check className="w-4 h-4 text-teal-300" /> Référent unique
                </span>
                <span className="flex items-center gap-2 pt-3">
                  <Check className="w-4 h-4 text-teal-300" /> Facturation pro
                </span>
                <span className="flex items-center gap-2 pt-3">
                  <Check className="w-4 h-4 text-teal-300" /> Devis sous 24h
                </span>
                <span className="flex items-center gap-2 pt-3">
                  <Check className="w-4 h-4 text-teal-300" /> Astreinte 24/7
                </span>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="grid grid-cols-2 gap-3">
                {VALUE_PROPS.map((v) => (
                  <div
                    key={v.t}
                    data-testid={`pro-value-${v.t.toLowerCase().replace(/[^a-z]/g, "-")}`}
                    className="bg-slate-800/80 backdrop-blur border border-slate-700 rounded-2xl p-5"
                  >
                    <v.icon className="w-6 h-6 text-teal-300 mb-3" />
                    <div className="font-display font-bold text-white text-lg leading-tight">
                      {v.t}
                    </div>
                    <div className="text-sm text-slate-400 mt-1.5 leading-relaxed">
                      {v.d}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pour qui ? */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
        <div className="max-w-2xl mb-12">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-700 mb-3">
            <span className="h-px w-8 bg-teal-700" /> Pour qui ?
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
            Conçu pour les gestionnaires
            <br />
            <span className="text-teal-600">de parcs immobiliers.</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {STRUCTURES.map((s) => (
            <div
              key={s.title}
              data-testid={`pro-structure-${s.title.toLowerCase().replace(/[^a-z]/g, "-").slice(0, 20)}`}
              className="bg-white border border-slate-200 rounded-3xl p-7 hover:border-teal-300 hover:shadow-xl hover:shadow-teal-900/5 transition-all group"
            >
              <div className="w-14 h-14 rounded-2xl bg-teal-50 text-teal-700 flex items-center justify-center mb-5 group-hover:bg-teal-600 group-hover:text-white transition-colors">
                <s.icon className="w-7 h-7" />
              </div>
              <h3 className="font-display text-xl font-bold text-slate-900 mb-2">
                {s.title}
              </h3>
              <p className="text-slate-600 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Services dédiés */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
          <div className="max-w-2xl mb-12">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-700 mb-3">
              <span className="h-px w-8 bg-teal-700" /> Services dédiés
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
              Tout ce dont vous avez besoin,
              <br />
              <span className="text-teal-600">sans intermédiaire.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PRO_SERVICES.map((s) => (
              <div
                key={s.t}
                data-testid={`pro-service-${s.t.toLowerCase().replace(/[^a-z]/g, "-").slice(0, 30)}`}
                className="bg-white border border-slate-200 rounded-3xl p-6"
              >
                <s.icon className="w-7 h-7 text-teal-600 mb-4" />
                <div className="font-display font-bold text-slate-900 text-lg mb-1.5">
                  {s.t}
                </div>
                <div className="text-sm text-slate-600 leading-relaxed">
                  {s.d}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
        <div className="max-w-2xl mb-12">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-700 mb-3">
            <span className="h-px w-8 bg-teal-700" /> Comment ça marche
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
            Démarrer un partenariat
            <br />
            <span className="text-teal-600">en 4 étapes.</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {STEPS.map((s, i) => (
            <div
              key={s.t}
              data-testid={`pro-step-${i + 1}`}
              className="bg-white border border-slate-200 rounded-3xl p-7 relative overflow-hidden"
            >
              <div className="absolute -top-6 -right-4 font-display font-black text-slate-100 text-8xl leading-none select-none">
                {i + 1}
              </div>
              <div className="relative">
                <div className="w-10 h-10 rounded-xl bg-teal-600 text-white flex items-center justify-center mb-4 font-display font-extrabold">
                  {i + 1}
                </div>
                <h3 className="font-display text-lg font-bold text-slate-900 mb-2">
                  {s.t}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">{s.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Partenaires & témoignages B2B */}
      <section
        data-testid="pro-partners-section"
        className="bg-white border-t border-slate-200"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-700 mb-3">
                <BadgeCheck className="w-4 h-4" /> Partenaires actuels
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
                Ils nous font confiance
                <br />
                <span className="text-teal-600">au quotidien.</span>
              </h2>
            </div>
            <div className="lg:col-span-5 flex items-end">
              <div className="rounded-3xl bg-slate-50 border border-slate-200 p-6 w-full">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-widest text-teal-700 mb-1">
                      Conciergeries · Agences · Hôtellerie
                    </div>
                    <div className="text-sm text-slate-600">
                      Témoignages de gestionnaires qui travaillent avec nous
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-4xl font-extrabold text-slate-900 leading-none">
                      {PRO_PARTNERS.length}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mt-1">
                      Témoignages
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
            {PRO_PARTNERS.map((p, i) => (
              <article
                key={p.company}
                data-testid={`pro-partner-${i + 1}`}
                className="group relative rounded-3xl border border-slate-200 bg-white p-7 flex flex-col transition-all hover:-translate-y-0.5 hover:shadow-xl hover:border-teal-300"
              >
                <Quote className="absolute top-5 right-5 w-8 h-8 text-slate-200 group-hover:text-teal-200 transition-colors" />

                <div className="flex items-center gap-1 mb-5">
                  {[...Array(p.rating)].map((_, idx) => (
                    <Star
                      key={idx}
                      className="w-4 h-4 fill-amber-400 text-amber-400"
                    />
                  ))}
                </div>

                <p className="text-slate-700 leading-relaxed mb-6 flex-1">
                  « {p.text} »
                </p>

                <div className="mt-auto pt-5 border-t border-slate-100 flex items-center gap-3">
                  <div
                    className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${p.accent} text-white font-display font-extrabold flex items-center justify-center text-sm flex-shrink-0`}
                  >
                    {p.initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-extrabold text-slate-900 truncate">
                      {p.company}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {p.contact} · {p.role}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-teal-700 font-bold mt-1">
                      {p.city}
                    </div>
                  </div>
                </div>

                <div className="absolute -top-3 left-7">
                  <span className="inline-flex items-center gap-1.5 bg-teal-600 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full shadow-lg">
                    <BadgeCheck className="w-3 h-3" /> {p.badge}
                  </span>
                </div>
              </article>
            ))}
          </div>

          <p
            data-testid="pro-partners-disclaimer"
            className="text-xs text-slate-500 mt-8 italic"
          >
            Témoignages issus de partenaires actifs — accord de partage obtenu.
            Identités complètes communicables sur demande.
          </p>
        </div>
      </section>

      {/* P1 — Services pros par ville × métier (SEO long-tail) */}
      <section
        data-testid="pro-cities-matrix"
        className="bg-slate-50 border-y border-slate-200"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
          <div className="max-w-2xl mb-10">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-700 mb-3">
              <span className="h-px w-8 bg-teal-700" /> Pros · Toutes les villes
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
              Serrurier pro
              <br />
              <span className="text-teal-600">dans 19 villes 22 & 35.</span>
            </h2>
            <p className="text-slate-600 mt-4 leading-relaxed">
              Trouvez votre serrurier dédié par métier et par commune sur
              toute la Côte d'Émeraude — un clic pour accéder à la page de
              votre ville.
            </p>
          </div>

          <div className="space-y-10">
            {PRO_AUDIENCES.map((aud) => (
              <div
                key={aud.slug}
                data-testid={`pro-matrix-${aud.slug}`}
              >
                <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
                  <h3 className="font-display text-xl md:text-2xl font-extrabold text-slate-900">
                    Serrurier pour{" "}
                    <span className={aud.accentText}>{aud.keyword}</span>
                  </h3>
                  <Link
                    to={`/serrurier-${aud.slug}`}
                    className="inline-flex items-center gap-2 text-sm text-teal-700 font-bold hover:underline"
                  >
                    Voir la page dédiée <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2.5">
                  {aud.cities.slice(0, 12).map((slug) => {
                    const c = CITIES.find((x) => x.slug === slug);
                    if (!c) return null;
                    return (
                      <Link
                        key={`${aud.slug}-${slug}`}
                        to={`/serrurier-${c.slug}`}
                        data-testid={`matrix-link-${aud.slug}-${c.slug}`}
                        className="group bg-white border border-slate-200 rounded-xl px-3 py-2.5 hover:border-teal-500 hover:shadow-sm transition-all"
                      >
                        <div className="text-[11px] uppercase tracking-widest text-slate-400 font-bold">
                          {aud.keyword.split(" ")[0]}
                        </div>
                        <div className="font-display font-bold text-slate-900 text-xs md:text-sm group-hover:text-teal-700 leading-snug truncate">
                          {c.name}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <p className="text-xs text-slate-500 mt-10 italic">
            Couverture sur 19 communes des Côtes-d'Armor (22) et de
            l'Ille-et-Vilaine (35) — rayon de 30 km autour de Mont-Dol.
          </p>
        </div>
      </section>

      {/* FAQ Pro */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-20 md:py-24">
          <div className="max-w-2xl mb-10">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-700 mb-3">
              <span className="h-px w-8 bg-teal-700" /> Questions fréquentes
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
              Tout ce que les pros nous demandent.
            </h2>
          </div>
          <div className="space-y-3">
            {PRO_FAQ.map((f, i) => (
              <details
                key={i}
                data-testid={`pro-faq-${i + 1}`}
                className="group bg-white border border-slate-200 rounded-2xl px-6 py-5 open:shadow-lg open:border-teal-200 transition-all"
              >
                <summary className="flex items-center justify-between gap-4 cursor-pointer list-none">
                  <span className="font-display font-bold text-slate-900 text-base md:text-lg">
                    {f.q}
                  </span>
                  <span className="w-8 h-8 rounded-full bg-slate-100 group-open:bg-teal-600 group-open:text-white text-slate-500 flex items-center justify-center transition-colors flex-shrink-0">
                    <ArrowRight className="w-4 h-4 group-open:rotate-90 transition-transform" />
                  </span>
                </summary>
                <p className="mt-4 text-slate-600 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Brochure download banner */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-12 -mt-8 md:-mt-12">
        <div
          data-testid="pro-brochure-banner"
          className="rounded-3xl bg-gradient-to-br from-teal-50 to-white border border-teal-200 p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-5 md:gap-6"
        >
          <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-teal-600 text-white flex items-center justify-center flex-shrink-0">
            <FileText className="w-7 h-7 md:w-8 md:h-8" />
          </div>
          <div className="flex-1">
            <div className="text-xs font-bold uppercase tracking-widest text-teal-700 mb-1">
              Plaquette PDF · 1 page A4
            </div>
            <h3 className="font-display text-xl md:text-2xl font-extrabold text-slate-900 leading-tight">
              Présentez notre offre à votre direction.
            </h3>
            <p className="text-slate-600 text-sm mt-1.5 leading-relaxed">
              Téléchargez notre plaquette synthétique : services, process,
              valeurs ajoutées et coordonnées en un seul document à partager
              en interne ou en réunion.
            </p>
          </div>
          <a
            data-testid="pro-brochure-download"
            href={`${API}/pro/brochure.pdf`}
            target="_blank"
            rel="noopener noreferrer"
            download="mon-serrurier-breton-offre-pros.pdf"
            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-full px-6 py-3.5 transition-colors flex-shrink-0"
          >
            <Download className="w-4 h-4" /> Télécharger le PDF
          </a>
        </div>
      </section>

      {/* Form */}
      <section
        id="pro-form"
        className="bg-slate-900 text-white scroll-mt-32"
        data-testid="pro-form-section"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5 space-y-6">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-300">
                <span className="h-px w-8 bg-teal-300" /> Demande de partenariat
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight leading-tight">
                Discutons de votre
                <br />
                <span className="text-teal-300">parc immobilier.</span>
              </h2>
              <p className="text-slate-300 leading-relaxed">
                Décrivez-nous votre activité et vos besoins récurrents. Nous
                vous recontactons sous <strong className="text-white">24 heures ouvrées</strong>{" "}
                avec une proposition cadre adaptée.
              </p>
              <div className="space-y-3 pt-2">
                <div className="flex items-center gap-3 text-slate-300">
                  <Phone className="w-4 h-4 text-teal-300" />
                  <a
                    href={`tel:${COMPANY.phoneDigits}`}
                    className="font-display font-bold text-white hover:text-teal-300"
                    data-testid="pro-direct-phone"
                  >
                    {COMPANY.phone}
                  </a>
                  <span className="text-slate-500 text-sm">— ligne directe</span>
                </div>
                <div className="flex items-center gap-3 text-slate-300">
                  <Clock className="w-4 h-4 text-teal-300" />
                  <span>Réponse jours ouvrés sous 1h</span>
                </div>
                <div className="flex items-center gap-3 text-slate-300">
                  <ShieldCheck className="w-4 h-4 text-teal-300" />
                  <span>Confidentialité garantie — données strictement usage interne</span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              {sent ? (
                <div
                  data-testid="pro-form-success"
                  className="bg-white text-slate-900 rounded-3xl p-8 md:p-10 border border-teal-200"
                >
                  <div className="w-14 h-14 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center mb-4">
                    <Check className="w-7 h-7" />
                  </div>
                  <h3 className="font-display text-2xl font-extrabold mb-2">
                    Demande bien reçue.
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    Nous vous recontactons sous 24 heures ouvrées pour
                    organiser un audit gratuit et vous proposer une grille
                    tarifaire adaptée à votre parc.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSent(false)}
                    className="mt-6 inline-flex items-center gap-2 text-teal-700 font-semibold hover:underline"
                    data-testid="pro-form-reset"
                  >
                    Envoyer une autre demande <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  data-testid="pro-form"
                  className="bg-white text-slate-900 rounded-3xl p-6 md:p-8 space-y-4"
                  noValidate
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                        Nom & prénom *
                      </label>
                      <input
                        type="text"
                        value={form.nom}
                        onChange={update("nom")}
                        className={inputCls("nom")}
                        placeholder="Jean Dupont"
                        data-testid="pro-input-nom"
                      />
                      {errors.nom && (
                        <p className="text-red-600 text-xs mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> {errors.nom}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                        Entreprise *
                      </label>
                      <input
                        type="text"
                        value={form.entreprise}
                        onChange={update("entreprise")}
                        className={inputCls("entreprise")}
                        placeholder="Cabinet Dupont Immobilier"
                        data-testid="pro-input-entreprise"
                      />
                      {errors.entreprise && (
                        <p className="text-red-600 text-xs mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> {errors.entreprise}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                        Fonction
                      </label>
                      <input
                        type="text"
                        value={form.fonction}
                        onChange={update("fonction")}
                        className={inputCls("fonction")}
                        placeholder="Gestionnaire / Directeur"
                        data-testid="pro-input-fonction"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                        Type de structure *
                      </label>
                      <select
                        value={form.type_structure}
                        onChange={update("type_structure")}
                        className={inputCls("type_structure")}
                        data-testid="pro-input-type"
                      >
                        <option value="">— sélectionner —</option>
                        {STRUCTURE_OPTIONS.map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </select>
                      {errors.type_structure && (
                        <p className="text-red-600 text-xs mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />{" "}
                          {errors.type_structure}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                        Téléphone *
                      </label>
                      <input
                        type="tel"
                        value={form.telephone}
                        onChange={update("telephone")}
                        className={inputCls("telephone")}
                        placeholder="07 80 93 93 00"
                        data-testid="pro-input-telephone"
                      />
                      {errors.telephone && (
                        <p className="text-red-600 text-xs mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> {errors.telephone}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                        Email pro *
                      </label>
                      <input
                        type="email"
                        value={form.email}
                        onChange={update("email")}
                        className={inputCls("email")}
                        placeholder="contact@entreprise.fr"
                        data-testid="pro-input-email"
                      />
                      {errors.email && (
                        <p className="text-red-600 text-xs mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> {errors.email}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                      Nombre de lots / chambres / sites
                    </label>
                    <input
                      type="text"
                      value={form.nb_lots}
                      onChange={update("nb_lots")}
                      className={inputCls("nb_lots")}
                      placeholder="ex : 45 lots, 22 chambres, 3 sites…"
                      data-testid="pro-input-lots"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                      Décrivez votre besoin *
                    </label>
                    <textarea
                      rows={5}
                      value={form.besoin}
                      onChange={update("besoin")}
                      className={inputCls("besoin")}
                      placeholder="Volumes mensuels estimés, types d'interventions récurrentes, attentes spécifiques…"
                      data-testid="pro-input-besoin"
                    />
                    {errors.besoin && (
                      <p className="text-red-600 text-xs mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> {errors.besoin}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="pro-form-submit"
                    className="w-full inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-60 text-white font-bold rounded-full px-6 py-4 transition-colors"
                  >
                    {loading ? (
                      "Envoi en cours…"
                    ) : (
                      <>
                        <Send className="w-4 h-4" /> Envoyer ma demande
                      </>
                    )}
                  </button>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    En envoyant ce formulaire, vous acceptez d'être recontacté
                    par Mon Serrurier Breton pour traiter votre demande. Vos
                    données ne sont jamais partagées —{" "}
                    <Link
                      to="/mentions-legales"
                      className="text-teal-700 hover:underline"
                    >
                      mentions légales
                    </Link>
                    .
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
